import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { Hint, Label, Error } from '@progress/kendo-react-labels';
import React, { useRef } from 'react';

interface GearFormWrapperProps extends FieldRenderProps {
  children: React.ReactNode;
  tooltipMessage?: string;
}

const getValidationState = (
  touched: boolean,
  modified: boolean,
  visited: boolean,
  validationMessage: string | null | undefined,
  id: string
) => {
  const shouldShow = (touched || modified || visited) && validationMessage;
  return {
    showValidationMessage: !!shouldShow,
    validationMessage: shouldShow ? validationMessage : null,
    errorId: shouldShow ? `${id}_error` : '',
    errorLabelClass: shouldShow ? 'k-text-error' : ''
  };
};

const getHintState = (showValidationMessage: boolean, hint: string, id: string) => {
  const shouldShow = !showValidationMessage && hint;
  return {
    showHint: shouldShow,
    hintId: shouldShow ? `${id}_hint` : ''
  };
};

const formatLabel = (label: string, optional: boolean) => `${label}${optional === false ? ' *' : ''}`;

const getLoadingClassName = (isLoading: boolean) =>
  `fieldLoadingContainer${isLoading ? ' fieldLoadingContainer--active' : ''}`;

export const GearFieldWrapper: React.FC<GearFormWrapperProps> = props => {
  const {
    validationMessage,
    touched,
    id,
    valid,
    disabled,
    hint,
    label,
    wrapperStyle,
    optional,
    modified,
    visited,
    tooltipMessage,
    isLoading = false,
    children
  } = props;

  const editorRef = useRef<HTMLDivElement>(null);

  const validation = getValidationState(touched, modified, visited, validationMessage, id);
  const hintState = getHintState(validation.showValidationMessage, hint, id);
  const labelId = label ? `${id}_label` : '';

  return (
    <FieldWrapper style={wrapperStyle}>
      {label && (
        <div title={tooltipMessage}>
          <Label
            id={labelId}
            editorRef={editorRef}
            editorId={id}
            editorValid={valid}
            editorDisabled={disabled}
            className={`k-form-label ${validation.errorLabelClass}`}
          >
            {formatLabel(label, optional)}
          </Label>
        </div>
      )}

      <div className="k-form-field-wrap" ref={editorRef}>
        <div className={getLoadingClassName(isLoading)}>
          {children}
          {isLoading && <span className="fieldLoadingSpinner k-icon k-i-loading" />}
        </div>

        {hintState.showHint && <Hint id={hintState.hintId}>{hint}</Hint>}
        {validation.showValidationMessage && <Error id={validation.errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  );
};
